import { addToast } from "@heroui/react"

/**
 * Toast Service
 * Centralized service for displaying toast notifications throughout the application
 */
export class ToastService {
    /**
     * Show a success toast notification
     * @param message - The message to display
     * @param title - Optional title for the toast
     */
    static success(message: string, title?: string): void {
        addToast({
            title: title || "Success",
            description: message,
            color: "success",
            timeout: 4000,
            classNames: {
                base: "rounded", // Add rounded corners to the toast
            }
        })
    }

    /**
     * Show an error toast notification
     * @param message - The error message to display
     * @param title - Optional title for the toast
     */
    static error(message: string, title?: string): void {
        addToast({
            title: title || "Error",
            description: message,
            color: "danger",
            timeout: 5000,
            classNames: {
                base: "rounded", // Add rounded corners to the toast
            }
        })
    }

    /**
     * Show a warning toast notification
     * @param message - The warning message to display
     * @param title - Optional title for the toast
     */
    static warning(message: string, title?: string): void {
        addToast({
            title: title || "Warning",
            description: message,
            color: "warning",
            timeout: 4500,
            classNames: {
                base: "rounded", // Add rounded corners to the toast
            }
        })
    }

    /**
     * Show an info toast notification
     * @param message - The info message to display
     * @param title - Optional title for the toast
     */
    static info(message: string, title?: string): void {
        addToast({
            title: title || "Info",
            description: message,
            color: "primary",
            timeout: 4000,
            classNames: {
                base: "rounded", // Add rounded corners to the toast
            }
        })
    }

    /**
     * Show a loading toast notification (stays until manually closed)
     * @param message - The loading message to display
     * @param title - Optional title for the toast
     * @returns The toast key that can be used to close it later
     */
    static loading(message: string, title?: string): string | null {
        return addToast({
            title: title || "Loading",
            description: message,
            color: "default",
            timeout: Infinity, // Stays until manually closed
            classNames: {
                base: "rounded", // Add rounded corners to the toast
            }
        })
    }

    /**
     * Show a custom toast notification
     * @param options - Custom toast options
     */
    static custom(options: {
        title: string
        message: string
        color?: "default" | "primary" | "secondary" | "success" | "warning" | "danger"
        timeout?: number
    }): void {
        addToast({
            title: options.title,
            description: options.message,
            color: options.color || "default",
            timeout: options.timeout || 4000,
            classNames: {
                base: "rounded", // Add rounded corners to the toast
            }
        })
    }
}
